import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categoryArticles, categoryFeatures } from '@/mocks/category';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const articles = useMemo(() => categoryArticles[slug || ''] || [], [slug]);

  const activeCategory = category || categories[0];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 51吃瓜网`,
    url: 'http://greendoc.cn/category/all',
    description: activeCategory.description,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://greendoc.cn/' },
        { '@type': 'ListItem', position: 2, name: activeCategory.name, item: 'http://greendoc.cn/category/all' },
      ],
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        {/* 分类头图 */}
        <section className="relative w-full min-h-[400px] md:min-h-[500px] flex items-end overflow-hidden">
          <img
            src={activeCategory.heroImage}
            alt={activeCategory.name}
            title={activeCategory.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/30" />
          <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-10 md:pb-14">
            <Breadcrumb items={[{ label: activeCategory.name, href: '/category/all' }]} />
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-background-50">{activeCategory.name}</h1>
            <p className="mt-3 text-base md:text-lg text-foreground-200 max-w-xl leading-relaxed">{activeCategory.description}</p>
            <div className="mt-6 flex items-center gap-6">
              {activeCategory.stats.map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl md:text-3xl font-black text-background-50">{stat.value}</p>
                  <p className="text-xs text-foreground-300 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 文章网格 */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              {activeCategory.name}精选
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to="/category/all"
                  className="group block bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-accent-300/40 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 text-xs font-medium text-accent-700 bg-background-50/90 backdrop-blur-sm rounded-full whitespace-nowrap">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-base font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
                      <span>
                        <i className="ri-time-line mr-1" />
                        {article.date}
                      </span>
                      <span>
                        <i className="ri-eye-line mr-1" />
                        {article.views}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 筛选特色区 */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <p className="text-xs font-medium text-accent-600 uppercase tracking-widest mb-2">平台特色</p>
            <h2 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight mb-10 md:mb-14">
              为什么选择
              <br />
              <span className="text-primary-500">51吃瓜网</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryFeatures.map((f, i) => (
                <div key={i} className="p-6 rounded-2xl bg-background-100 border border-background-200/40">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 mb-4">
                    <i className={`${f.icon} text-xl`} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">{f.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}